import { Box, Typography, useTheme } from '@mui/material'
import React, { memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'

import Accordion from '@/components/Accordion'
import type { LABColor } from '@/store/appSlice'

interface ColorValue {
  label: string
  value: string | number
}

interface DigitalColorMeterProps {
  labColor: LABColor
}

const ColorValueDisplay = memo<ColorValue>(({ label, value }) => {
  const theme = useTheme()

  return (
    <Box display="flex" alignItems="center">
      <Typography variant="body1" color="text.primary" sx={{ pr: '0.3rem' }}>
        {`${label} : `}
      </Typography>
      <Typography
        variant="body1"
        color="text.primary"
        sx={{
          borderBottom: `1px solid ${theme.palette.text.primary}`,
        }}
      >
        {value || '00.00'}
      </Typography>
    </Box>
  )
})

ColorValueDisplay.displayName = 'ColorValueDisplay'

const ColorCircle = memo<{ labColor: LABColor }>(({ labColor }) => {
  const theme = useTheme()

  return (
    <Box
      sx={{
        borderRadius: '50%',
        margin: '1.5rem auto',
        height: 'calc(6vh + 6vw)',
        width: 'calc(6vh + 6vw)',
        backgroundColor: `lab(${labColor?.L === '00.00' ? 100 : labColor?.L || 100} ${labColor?.a === '00.00' ? 0 : labColor?.a || 0} ${labColor?.b === '00.00' ? 0 : labColor?.b || 0})`,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          borderRadius: '50%',
          height: '50%',
          width: '50%',
          backgroundColor: theme.palette.background.paper,
        }}
      />
    </Box>
  )
})

ColorCircle.displayName = 'ColorCircle'

const labToLch = (L, a, b) => {
  const C = Math.sqrt(a * a + b * b)
  let h = Math.atan2(b, a) * (180 / Math.PI)
  if (h < 0) h += 360
  h = `${h.toFixed(2)}°` // Convert h to string with degree symbol
  return { L, C: +C.toFixed(2), h }
}

const DigitalColorMeter = memo<DigitalColorMeterProps>(({ labColor }) => {
  const location = useLocation()

  const values = useMemo(
    () =>
      ['/shade-guide-ai']?.includes(location.pathname)
        ? labToLch(labColor?.L || 100, labColor?.a || 0, labColor?.b || 0)
        : labColor,
    [labColor, location],
  )
  const theme = useTheme()
  const { t } = useTranslation('translation', { keyPrefix: 'gallery' })

  return (
    <Accordion
      title={t('digitalColorMeter')}
      sx={{
        borderTop: `1px solid ${theme.palette.text.primary}50`,
        px: '16px',
      }}
      titleSx={{ fontSize: '1.25rem' }}
    >
      <ColorCircle labColor={labColor} />
      <Box display="flex" justifyContent="space-evenly" mt={2}>
        {Object.entries(values).map(([key, value]) => (
          <ColorValueDisplay key={key} label={key} value={value} />
        ))}
      </Box>
    </Accordion>
  )
})

DigitalColorMeter.displayName = 'DigitalColorMeter'

export default DigitalColorMeter
